import React from "react";
import {
  fetchExistingWorkCodes,
  fetchTrades,
  fetchWorkCodeCandidates,
} from "../../../../api/clientV2";
import type { ExistingWorkCode, Trade, WorkCodeCandidate } from "../../../../models";
import { naturalCompare } from "../../../../utils/sort";
import CodeRow from "./CodeRow/CodeRow";
import MergeModal from "./MergeModal/MergeModal";
import TradeBuckets from "./TradeBuckets/TradeBuckets";
import type { BucketCode } from "./TradeBuckets/TradeBuckets";
import { buildCodeGroups } from "./codeGroups";
import type { CodeGroup, CodeSource } from "./codeGroups";
import {
  buildPairLookup,
  computeCurrentBySection,
  sectionKey,
  sectionsOfGroup,
  tradeOfGroupSection,
} from "./tradeResolution";
import type { TaktEdits, TradeEditEntry, TradePatch } from "../state";
import "./WorkCodesTab.css";

interface Props {
  scheduleId: number;
  edits: TaktEdits;
  /** Apply a patch to every source in a group, as ONE undo step. */
  onEditGroup: (sources: CodeSource[], patch: { newCode?: string; name?: string; color?: string }) => void;
  onMergeWorkCodes: (
    sources: CodeSource[],
    target: { code: string; name: string; color: string },
    tradeEntries: TradeEditEntry[],
  ) => void;
  /** Stage trade (re-)assignments by section-scoped key. */
  onSetWorkCodeTrade: (entries: TradeEditEntry[]) => void;
  /** Staged trade CRUD — drafts in the edit buffer, committed on save. */
  onCreateTrade: (draft: { name: string; fill_color: string; font_color: string }) => void;
  onUpdateTrade: (
    tradeId: number,
    patch: TradePatch,
    current: { name: string; fill_color: string; font_color: string },
  ) => void;
  onDeleteTrade: (tradeId: number) => void;
}

const NO_SECTION = "— no section —";

const WorkCodesTab: React.FC<Props> = ({
  scheduleId,
  edits,
  onEditGroup,
  onMergeWorkCodes,
  onSetWorkCodeTrade,
  onCreateTrade,
  onUpdateTrade,
  onDeleteTrade,
}) => {
  const [codes, setCodes] = React.useState<ExistingWorkCode[] | null>(null);
  const [serverTrades, setServerTrades] = React.useState<Trade[]>([]);
  const [query, setQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState<"code" | "name">("code");
  // Default to the on-board codes (≥1 non-completed carrier); historical codes
  // (all work done) stay out of the way until the super asks for them. A search
  // always spans everything, so old codes are still findable.
  const [showHistorical, setShowHistorical] = React.useState(false);
  const [bySection, setBySection] = React.useState(false);
  const [colorBy, setColorBy] = React.useState<"code" | "trade">("code");
  const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(new Set());
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [actsByGroup, setActsByGroup] = React.useState<Record<string, WorkCodeCandidate[]>>({});
  const [loadingGroups, setLoadingGroups] = React.useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = React.useState(false);
  const selAllRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    let live = true;
    fetchExistingWorkCodes(scheduleId).then((rows) => {
      if (live) setCodes(rows);
    });
    fetchTrades().then((rows) => {
      if (live) setServerTrades(rows);
    });
    return () => {
      live = false;
    };
  }, [scheduleId]);

  const groups = React.useMemo(
    () => buildCodeGroups(codes ?? [], edits.codeEdits),
    [codes, edits.codeEdits],
  );

  // --- Trades: the DISPLAYED list = server trades + the staged CRUD overlay
  // (renames/recolors applied, deleted hidden, staged-new appended with their
  // negative temp ids). ---
  const trades = React.useMemo(() => {
    const deleted = new Set(edits.tradesDeleted);
    const out: Trade[] = [];
    for (const t of serverTrades) {
      if (deleted.has(t.id)) continue;
      const u = edits.tradesUpdated[t.id];
      out.push(u ? { ...t, ...u } : t);
    }
    for (const c of edits.tradesCreated) {
      out.push({
        id: c.tempId,
        name: c.name,
        fill_color: c.fill_color,
        font_color: c.font_color,
        display_order: Number.MAX_SAFE_INTEGER,
        task_codes: [],
      });
    }
    return out;
  }, [serverTrades, edits.tradesCreated, edits.tradesUpdated, edits.tradesDeleted]);

  // Current (server) per-section mapping — what staged assignments diff against.
  const pairs = React.useMemo(() => buildPairLookup(codes ?? []), [codes]);
  const currentBySection = React.useMemo(
    () => computeCurrentBySection(codes ?? [], serverTrades),
    [codes, serverTrades],
  );
  const tradeById = React.useMemo(
    () => new Map(trades.map((t) => [t.id, t])),
    [trades],
  );

  // Resolve every group's per-section trade ONCE — the left row, the buckets,
  // and the assigned count all read from this rather than re-walking sources.
  const groupTrades = React.useMemo(() => {
    const m = new Map<
      string,
      { sections: string[]; bySection: Map<string, number | null>; mixed: boolean; single: number | null }
    >();
    for (const g of groups) {
      const sections = sectionsOfGroup(g, pairs);
      const bySection = new Map<string, number | null>();
      const distinct = new Set<number | null>();
      for (const section of sections) {
        const tid = tradeOfGroupSection(g, section, pairs, edits.tradeEdits, currentBySection);
        bySection.set(section, tid);
        distinct.add(tid);
      }
      const mixed = distinct.size > 1;
      m.set(g.key, {
        sections,
        bySection,
        mixed,
        single: mixed || sections.length === 0 ? null : Array.from(distinct)[0],
      });
    }
    return m;
  }, [groups, pairs, edits.tradeEdits, currentBySection]);

  // The left-list row's trade: a single trade across the code's sections, or
  // "mixed" when sections disagree.
  const tradeFor = React.useCallback(
    (g: CodeGroup): { trade: Trade | null; mixed: boolean } => {
      const r = groupTrades.get(g.key);
      if (!r || r.mixed) return { trade: null, mixed: !!r?.mixed };
      return { trade: r.single == null ? null : tradeById.get(r.single) ?? null, mixed: false };
    },
    [groupTrades, tradeById],
  );

  // One bucket entry per (group, section) that's in a trade.
  const codesByTrade = React.useMemo(() => {
    const m = new Map<number, BucketCode[]>();
    for (const g of groups) {
      const r = groupTrades.get(g.key);
      if (!r) continue;
      r.bySection.forEach((tid, section) => {
        // A trade staged-deleted reads as unassigned.
        if (tid == null || !tradeById.has(tid)) return;
        const arr = m.get(tid) ?? [];
        arr.push({
          id: g.key,
          code: g.code || "—",
          label: g.name || g.code,
          section: section || NO_SECTION,
        });
        m.set(tid, arr);
      });
    }
    return m;
  }, [groups, groupTrades, tradeById]);

  // Groups with at least one section in a (live) trade — the rest are "No trade".
  const assignedGroupCount = React.useMemo(() => {
    let n = 0;
    groupTrades.forEach((r) => {
      const inTrade = Array.from(r.bySection.values()).some(
        (tid) => tid != null && tradeById.has(tid),
      );
      if (inTrade) n += 1;
    });
    return n;
  }, [groupTrades, tradeById]);

  // When a rename/merge collapses sources into a new combined key, carry any
  // selection/expanded keys onto the surviving group (don't just drop them), so
  // an open/selected row stays open/selected through the merge.
  React.useEffect(() => {
    const valid = new Set(groups.map((g) => g.key));
    const sourceToKey = new Map<string, string>();
    for (const g of groups) {
      for (const s of g.sources) sourceToKey.set(`${s.code}|${s.prefix}`, g.key);
    }
    const remap = (prev: Set<string>): Set<string> => {
      const next = new Set<string>();
      for (const k of Array.from(prev)) {
        if (valid.has(k)) {
          next.add(k);
          continue;
        }
        // Stale key — its sources may now live under a different group's key.
        for (const part of k.split("¦")) {
          const nk = sourceToKey.get(part);
          if (nk) next.add(nk);
        }
      }
      const same = next.size === prev.size && Array.from(prev).every((k) => next.has(k));
      return same ? prev : next;
    };
    setExpanded(remap);
    setSelected(remap);
  }, [groups]);

  // Historical codes (no active carriers) that the on-board default hides.
  const historicalCount = React.useMemo(
    () => groups.filter((g) => g.activeCount === 0).length,
    [groups],
  );

  const shown = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    // A search OR "show historical" spans every code; otherwise the browse
    // scope is the on-board subset so old codes don't clutter the list.
    const base = showHistorical || q ? groups : groups.filter((g) => g.activeCount > 0);
    if (!q) return base;
    // An open (being-edited) row always stays — editing its name must never
    // filter it out from under you.
    return base.filter(
      (g) =>
        expanded.has(g.key) ||
        g.code.toLowerCase().includes(q) ||
        g.name.toLowerCase().includes(q),
    );
  }, [groups, query, expanded, showHistorical]);

  // Sort the shown list. "code" keeps the natural code order; "name" sorts
  // alphabetically (A–Z, case/number-aware) so near-duplicate names land next to
  // each other and are easy to spot + merge.
  const sorted = React.useMemo(() => {
    if (sortBy === "code") return shown;
    return shown
      .slice()
      .sort(
        (a, b) =>
          naturalCompare(a.name || a.code, b.name || b.code) ||
          naturalCompare(a.code, b.code),
      );
  }, [shown, sortBy]);

  // Longest code + name (in chars) among the shown rows, so the pencil + "N
  // activities" line up in a column after the longest name (not right-aligned).
  const colLens = React.useMemo(() => {
    let codeLen = 2;
    let nameLen = 3;
    for (const g of shown) {
      codeLen = Math.max(codeLen, (g.code || "—").length);
      nameLen = Math.max(nameLen, (g.name || g.code || "").length);
    }
    return { codeLen, nameLen };
  }, [shown]);

  // Selection / select-all over the SHOWN groups.
  const shownKeys = shown.map((g) => g.key);
  let selCount = 0;
  for (const k of shownKeys) if (selected.has(k)) selCount += 1;
  const allSel = shownKeys.length > 0 && selCount === shownKeys.length;
  const someSel = selCount > 0 && !allSel;
  React.useEffect(() => {
    if (selAllRef.current) selAllRef.current.indeterminate = someSel;
  }, [someSel]);

  const toggleSelect = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSel) shownKeys.forEach((k) => next.delete(k));
      else shownKeys.forEach((k) => next.add(k));
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  // --- Trade staging (whole-code drag/dropdown, per-section ✕ remove) ---

  /** Build the per-(source, section) trade entries for a code group: every
   *  section the group carries, or just `onlySection` when given (the ✕). */
  const tradeEntriesFor = React.useCallback(
    (g: CodeGroup, tradeId: number | null, onlySection?: string): TradeEditEntry[] => {
      const entries: TradeEditEntry[] = [];
      for (const s of g.sources) {
        const stc = pairs.get(`${s.code}|${s.prefix}`)?.section_task_codes ?? {};
        for (const section of Object.keys(stc)) {
          if (onlySection !== undefined && section !== onlySection) continue;
          const key = sectionKey(s.code, s.prefix, section);
          entries.push({ key, tradeId, current: currentBySection[key] ?? null });
        }
      }
      return entries;
    },
    [pairs, currentBySection],
  );

  /** Assign whole code groups (drag onto bucket / dropdown / unassign). */
  const stageTradeForKeys = React.useCallback(
    (keys: string[], tradeId: number | null) => {
      const entries: TradeEditEntry[] = [];
      for (const k of keys) {
        const g = groups.find((x) => x.key === k);
        if (g) entries.push(...tradeEntriesFor(g, tradeId));
      }
      onSetWorkCodeTrade(entries);
      // The staged rows are done — deselect them (an unrelated selection stays).
      setSelected((prev) => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        keys.forEach((k) => next.delete(k));
        return next.size === prev.size ? prev : next;
      });
    },
    [groups, tradeEntriesFor, onSetWorkCodeTrade],
  );

  /** Remove ONE section of a code group from its trade (✕ on a chip). */
  const removeFromSection = React.useCallback(
    (groupKey: string, section: string) => {
      const g = groups.find((x) => x.key === groupKey);
      if (!g) return;
      onSetWorkCodeTrade(
        tradeEntriesFor(g, null, section === NO_SECTION ? "" : section),
      );
    },
    [groups, tradeEntriesFor, onSetWorkCodeTrade],
  );

  const handleUpdateTrade = React.useCallback(
    (id: number, patch: TradePatch) => {
      const cur = serverTrades.find((t) => t.id === id);
      onUpdateTrade(
        id,
        patch,
        cur
          ? { name: cur.name, fill_color: cur.fill_color, font_color: cur.font_color }
          : { name: "", fill_color: "", font_color: "" },
      );
    },
    [serverTrades, onUpdateTrade],
  );

  const loadGroup = React.useCallback(
    async (g: CodeGroup) => {
      if (actsByGroup[g.key] || loadingGroups.has(g.key)) return;
      setLoadingGroups((p) => new Set(p).add(g.key));
      try {
        const codeVals = Array.from(new Set(g.sources.map((s) => s.code)));
        const results = await Promise.all(
          codeVals.map((c) => fetchWorkCodeCandidates(scheduleId, { code: c })),
        );
        const sourceSet = new Set(g.sources.map((s) => `${s.code}|${s.prefix}`));
        const acts: WorkCodeCandidate[] = [];
        for (const r of results) {
          for (const a of r.same_code_holders) {
            if (sourceSet.has(`${a.current_takt_work}|${a.current_takt_prefix}`)) acts.push(a);
          }
        }
        setActsByGroup((p) => ({ ...p, [g.key]: acts }));
      } finally {
        setLoadingGroups((p) => {
          const n = new Set(p);
          n.delete(g.key);
          return n;
        });
      }
    },
    [scheduleId, actsByGroup, loadingGroups],
  );

  const toggleExpand = (key: string) => {
    const g = groups.find((x) => x.key === key);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else {
        next.add(key);
        if (g) void loadGroup(g);
      }
      return next;
    });
  };

  const selectedGroups = groups.filter((g) => selected.has(g.key));

  // The trade a merge defaults to: the first selected code that's wholly in one
  // trade — so merging a traded code with an untraded one keeps the trade.
  // Computed only when the merge modal opens, hence a plain function.
  const mergeDefaultTrade = (): number | null => {
    for (const g of selectedGroups) {
      const single = groupTrades.get(g.key)?.single;
      if (single != null && tradeById.has(single)) return single;
    }
    return null;
  };

  const sections = React.useMemo(() => {
    if (!bySection) return [];
    const map = new Map<string, CodeGroup[]>();
    for (const g of sorted) {
      const secs = g.sections.length ? g.sections : [NO_SECTION];
      for (const s of secs) {
        const arr = map.get(s);
        if (arr) arr.push(g);
        else map.set(s, [g]);
      }
    }
    return Array.from(map.keys())
      .sort()
      .map((s) => ({ section: s, groups: map.get(s) as CodeGroup[] }));
  }, [bySection, sorted]);

  const toggleSection = (s: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  const allSectionsCollapsed =
    sections.length > 0 && sections.every((s) => collapsedSections.has(s.section));

  // "Collapse all" closes every row opened for edit (and, when grouped, every
  // section). Enabled whenever anything is open.
  const anythingExpanded =
    expanded.size > 0 || (bySection && sections.length > 0 && !allSectionsCollapsed);
  const collapseAll = () => {
    setExpanded(new Set());
    if (bySection && sections.length > 0) {
      setCollapsedSections(new Set(sections.map((s) => s.section)));
    }
  };

  // `section` is set only in the group-by-section view, where a row sits under
  // ONE section — its trade chip + dropdown are then scoped to that section
  // (not the union across all of the code's sections). NO_SECTION → "".
  const renderRow = (g: CodeGroup, keyPrefix = "", section?: string) => {
    let tf: { trade: Trade | null; mixed: boolean };
    let onSetTrade: (tradeId: number | null) => void;
    if (section !== undefined) {
      const sec = section === NO_SECTION ? "" : section;
      const tid = groupTrades.get(g.key)?.bySection.get(sec) ?? null;
      tf = { trade: tid == null ? null : tradeById.get(tid) ?? null, mixed: false };
      onSetTrade = (tradeId) => onSetWorkCodeTrade(tradeEntriesFor(g, tradeId, sec));
    } else {
      tf = tradeFor(g);
      onSetTrade = (tradeId) => stageTradeForKeys([g.key], tradeId);
    }
    return (
      <CodeRow
        key={`${keyPrefix}${g.key}`}
        group={g}
        selected={selected.has(g.key)}
        expanded={expanded.has(g.key)}
        loading={loadingGroups.has(g.key)}
        activities={actsByGroup[g.key]}
        trades={trades}
        trade={tf.trade}
        tradeMixed={tf.mixed}
        colorByTrade={colorBy === "trade"}
        onToggleSelect={toggleSelect}
        onToggleExpand={toggleExpand}
        onEditGroup={onEditGroup}
        onSetTrade={onSetTrade}
        getDragIds={(key) => (selected.has(key) ? Array.from(selected) : [key])}
      />
    );
  };

  /** Small labeled two-option segmented control for the view bar. */
  const seg = <T extends string>(
    label: string,
    value: T,
    options: { v: T; text: string; title: string }[],
    onPick: (v: T) => void,
  ) => (
    <span className="takt-editor-codes-segwrap">
      <span className="takt-editor-codes-view-label">{label}</span>
      <div className="takt-editor-codes-seg" role="group" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            className={`takt-editor-codes-seg-opt ${value === o.v ? "is-on" : ""}`}
            onClick={() => onPick(o.v)}
            title={o.title}
          >
            {o.text}
          </button>
        ))}
      </div>
    </span>
  );

  return (
    <div className="takt-editor-codes">
      <div className="takt-editor-codes-main">
        <div className="takt-editor-codes-head">
          <span className="takt-editor-codes-searchwrap">
            <svg
              className="takt-editor-codes-searchicon"
              viewBox="0 0 16 16"
              width="13"
              height="13"
              aria-hidden
            >
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                d="M7 1.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM11 11l3.5 3.5"
              />
            </svg>
            <input
              className="takt-editor-codes-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code or name…"
              aria-label="Search work codes"
            />
            {query && (
              <button
                type="button"
                className="takt-editor-codes-searchclear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </span>

          <span className="takt-editor-codes-headsep" aria-hidden />

          {/* View controls — three parallel choices about how the list READS:
              sort order, section grouping, and what drives the colors. */}
          <div className="takt-editor-codes-view">
            {seg("Sort", sortBy, [
              { v: "code" as const, text: "Code", title: "Sort by code value" },
              {
                v: "name" as const,
                text: "A–Z",
                title:
                  "Sort by name A–Z — clusters near-duplicate names so you can spot and merge them",
              },
            ], setSortBy)}

            {seg("Group", bySection ? "section" : "none", [
              { v: "none" as const, text: "None", title: "One flat list" },
              {
                v: "section" as const,
                text: "Section",
                title:
                  "Group the list by Takt section (a code in several sections shows in each)",
              },
            ], (v) => setBySection(v === "section"))}

            {seg("Color", colorBy, [
              { v: "code" as const, text: "Code", title: "Pills show each work code's color" },
              {
                v: "trade" as const,
                text: "Trade",
                title: "Tint each row with its trade's color — unassigned rows stay plain",
              },
            ], setColorBy)}
          </div>

          <span className="takt-editor-codes-headsep" aria-hidden />

          <span className="takt-editor-codes-countwrap">
            <span className="takt-editor-codes-count">{shown.length} codes</span>
            {historicalCount > 0 && !query.trim() && (
              <button
                type="button"
                className="takt-editor-codes-scope"
                onClick={() => setShowHistorical((v) => !v)}
                title={
                  showHistorical
                    ? "Show only codes on the board (with active work)"
                    : "Also show historical codes whose work is all complete (search always finds them)"
                }
              >
                {showHistorical ? "On board only" : `Show historical (${historicalCount})`}
              </button>
            )}
          </span>
        </div>

        <div className={`takt-editor-codes-actions ${selected.size === 0 ? "is-empty" : ""}`}>
          <label className="takt-editor-codes-selectall-label">
            <input
              ref={selAllRef}
              type="checkbox"
              className="takt-editor-codes-selectall"
              checked={allSel}
              disabled={shownKeys.length === 0}
              onChange={toggleSelectAll}
              aria-label="Select all shown work codes"
            />
            Select all
          </label>
          <span className="takt-editor-codes-divider" aria-hidden />
          <span className="takt-editor-codes-selcount" data-empty={selected.size === 0}>
            {selected.size} selected
          </span>
          <button
            type="button"
            className="takt-editor-codes-clear"
            onClick={clearSelection}
            disabled={selected.size === 0}
          >
            Clear
          </button>
          <button
            type="button"
            className="takt-editor-codes-collapse"
            onClick={collapseAll}
            disabled={!anythingExpanded}
            title="Collapse every row opened for edit (and any expanded sections)"
          >
            Collapse all
          </button>
          <button
            type="button"
            className="takt-editor-codes-merge"
            disabled={selected.size < 2}
            onClick={() => setMergeOpen(true)}
            title={selected.size < 2 ? "Select 2+ codes to merge" : "Merge the selected codes into one"}
          >
            Merge Codes
          </button>
          <span className="takt-editor-codes-hint">
            Select codes to merge, or drag them onto a trade
          </span>
        </div>

        <div
          className="takt-editor-codes-list"
          style={
            {
              ["--takt-code-len" as string]: colLens.codeLen,
              ["--takt-code-name-len" as string]: colLens.nameLen,
            } as React.CSSProperties
          }
        >
          {codes === null && <div className="takt-editor-hint">Loading…</div>}
          {codes !== null && shown.length === 0 && (
            <div className="takt-editor-hint">No work codes match.</div>
          )}
          {codes !== null &&
            shown.length > 0 &&
            (bySection
              ? sections.map(({ section, groups: gs }) => {
                  // While searching, every section that has a match opens so the
                  // hits are visible; the manual collapse state is restored when
                  // the search clears (we don't mutate it).
                  const collapsed = !query.trim() && collapsedSections.has(section);
                  return (
                    <div key={section} className="takt-editor-codes-section">
                      <div
                        className="takt-editor-codes-section-head"
                        onClick={() => toggleSection(section)}
                      >
                        <span className="takt-editor-codes-section-chevron">
                          {collapsed ? "▸" : "▾"}
                        </span>
                        {section}
                        <span className="takt-editor-codes-section-count">{gs.length}</span>
                      </div>
                      {!collapsed && gs.map((g) => renderRow(g, `${section}|`, section))}
                    </div>
                  );
                })
              : sorted.map((g) => renderRow(g)))}
        </div>
      </div>

      <TradeBuckets
        trades={trades}
        codesByTrade={codesByTrade}
        unassignedCount={Math.max(0, groups.length - assignedGroupCount)}
        onAssign={(ids, tradeId) => stageTradeForKeys(ids, tradeId)}
        onUnassign={(ids) => stageTradeForKeys(ids, null)}
        onRemoveFromSection={removeFromSection}
        onAddTrade={(name, fill, font) =>
          onCreateTrade({ name, fill_color: fill, font_color: font })
        }
        onEditTrade={handleUpdateTrade}
        onDeleteTrade={onDeleteTrade}
      />

      {mergeOpen && selectedGroups.length >= 2 && (
        <MergeModal
          groups={selectedGroups}
          trades={trades}
          defaultTradeId={mergeDefaultTrade()}
          onMerge={(sources, target, tradeId) => {
            // Put the whole merged code in one trade: stage every source's
            // section to the chosen trade (no-ops self-clear).
            const entries: TradeEditEntry[] = [];
            for (const g of selectedGroups) {
              entries.push(...tradeEntriesFor(g, tradeId));
            }
            onMergeWorkCodes(sources, target, entries);
            clearSelection();
          }}
          onClose={() => setMergeOpen(false)}
        />
      )}
    </div>
  );
};

export default WorkCodesTab;
